/* Какой дверью этот логин входил в прошлый раз.
 *
 * Экран входа один на две роли: администратор и клиентский кабинет. Форма всегда пробовала
 * админский вход, а на `invalid_credentials` повторяла те же данные на клиентском — иначе
 * провайдеру было бы некуда нажимать. Цена этой развилки вскрылась 17.09.2026: неверный пароль
 * засчитывается бэкендом на КАЖДОМ из двух запросов, так что одно нажатие стоит двух неудачных
 * попыток. Пять неудач по паре «логин + адрес» за четверть часа — отказ на 15 минут, десять за
 * сутки — блокировка на сутки (`enforce_failed_login_budget`), то есть владелец запирал себе
 * админку пятью нажатиями вместо десяти.
 *
 * Подсказка снимает развилку у того, кто уже входил: логин, который в прошлый раз оказался
 * админским, больше не стучится в клиентскую дверь, и наоборот. Незнакомый логин работает как
 * раньше — обе двери по очереди.
 *
 * Хранится рядом с запомненным логином и по тому же правилу: только при включённой галочке
 * «запомнить». Секрета здесь нет — роль логина узнаётся одной попыткой входа, — но и писать в
 * хранилище что-то про человека, который просил не запоминать, незачем.
 */

const REMEMBER_KEY = 'tt-remembered-login';

/** Дверь входа: админская или клиентского кабинета. */
export type LoginEndpoint = 'admin' | 'client';

interface Remembered {
  username: string;
  endpoint?: LoginEndpoint;
}

function read(): Remembered | null {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      username?: unknown;
      endpoint?: unknown;
      password?: unknown;
    };
    if (typeof parsed.username !== 'string') return null;
    const endpoint =
      parsed.endpoint === 'admin' || parsed.endpoint === 'client' ? parsed.endpoint : undefined;
    // Миграция: стереть пароль, если его сюда клала ранняя версия этой фичи.
    if ('password' in parsed) {
      localStorage.setItem(REMEMBER_KEY, JSON.stringify({ username: parsed.username, endpoint }));
    }
    return { username: parsed.username, endpoint };
  } catch {
    return null;
  }
}

/** Запомненный логин для подстановки в поле. */
export function loadRememberedUsername(): string | null {
  return read()?.username ?? null;
}

/**
 * Дверь, которой этот логин входил в прошлый раз, или `undefined` — если логин незнакомый.
 * Сравнение без учёта регистра: бэкенд ищет пользователя через `LOWER(username)`.
 */
export function endpointHintFor(username: string): LoginEndpoint | undefined {
  const remembered = read();
  if (!remembered?.endpoint) return undefined;
  return remembered.username.toLowerCase() === username.trim().toLowerCase()
    ? remembered.endpoint
    : undefined;
}

/** Записать (или стереть) логин и его дверь после успешного входа. */
export function rememberLogin(username: string, endpoint: LoginEndpoint, remember: boolean): void {
  if (!remember) {
    localStorage.removeItem(REMEMBER_KEY);
    return;
  }
  localStorage.setItem(REMEMBER_KEY, JSON.stringify({ username, endpoint }));
}
